<div class="flex flex-col gap-(--gap-relaxed)">
  @if (!isReadOnly()) {
    <div class="py-(--space-1) flex gap-(--gap-normal) items-center flex-wrap">
      <ces-button-secondary size="small" [label]="'common.buttons.plan' | transloco" icon="event" (onClick)="openPlanningDialog()" />
    </div>
  }

  @if (!pageStore.isAuscultationReady()) {
    <ces-grid-skeleton [columns]="skeletonColumns()" [lines]="10" [widths]="skeletonWidths" />
  }

  @if (pageStore.isAuscultationReady() && ausc.auscultationValues().length === 0) {
    <div class="text-center text-(color:--text-secondary) italic py-(--space-8)">
      {{ 'entities.auscultationInformation.messages.noData' | transloco }}
    </div>
  }

  <input #fileInput type="file" style="display: none" (change)="onFileSelected($event)" />

  @if (pageStore.isAuscultationReady() && ausc.auscultationValues().length > 0) {
    <p-table #dt [value]="tableData()" class="mt-(--space-2)" dataKey="campaignInfo.id">
      <ng-template #header>
        <tr>
          <th pSortableColumn="info.name">
            <div class="flex items-center gap-(--gap-normal)">{{ 'common.labels.name' | transloco }}</div>
          </th>
          <th pSortableColumn="info.priority">
            <div class="flex items-center gap-(--gap-normal)">{{ 'common.labels.mandatory' | transloco }}</div>
          </th>
          <th pSortableColumn="locationNames">
            <div class="flex items-center gap-(--gap-normal)">{{ 'common.labels.locations' | transloco }}</div>
          </th>
          <th>{{ 'common.labels.value' | transloco }}</th>
        </tr>
      </ng-template>
      <ng-template #body let-auscultationValue>
        <tr>
          <td class="align-middle">
            @if (hasAuscultationReadAccess() && pageStore.assetId() && auscultationValue.info.id) {
              <a
                [href]="
                  '/assets/' + pageStore.assetId() + '/configuration/measures/auscultation-information/' + auscultationValue.info.id
                "
                target="_blank"
                rel="noopener"
                class="underline text-(color:--p-text-color) hover:text-(color:--p-primary-color)">
                {{ auscultationValue.info.name }}
              </a>
            } @else {
              {{ auscultationValue.info.name }}
            }
            @if (t.testMode()) {
              <div class="text-xs font-mono rounded-(--radius-md) p-(--space-1) mt-(--space-1)">
                - Campaign id #{{ auscultationValue.campaignInfo.campaignId }}<br />
                - Campaign-Info id #{{ auscultationValue.campaignInfo.id }}<br />
                - Info id #{{ auscultationValue.info.id }}<br />
                - Info type #{{ auscultationValue.info.type }}<br />
                - Info priority #{{ auscultationValue.info.priority }}<br />
                - Location ids {{ auscultationValue.campaignInfo.locationsIds | json }}<br />
                - Read-only: {{ isReadOnly() ? ('common.buttons.yes' | transloco) : ('common.buttons.no' | transloco) }}<br />
              </div>
            }
          </td>
          <td class="align-middle">
            <ces-icon
              size="2xl"
              [attr.aria-label]="
                (auscultationValue.info.priority === true ? 'common.labels.mandatory' : 'common.labels.notMandatory') | transloco
              ">
              {{ auscultationValue.info.priority === true ? 'check_circle' : 'cancel' }}
            </ces-icon>
          </td>
          <td class="align-middle">
            {{ auscultationValue.locationNames }}
          </td>
          <td class="align-middle">
            <div class="flex flex-col gap-(--gap-normal)">
              @if (auscultationValue.info.type === InfoType.TEXT) {
                <div>
                  @if (isReadOnly()) {
                    <div class="whitespace-pre-wrap text-sm">
                      {{ getCurrentTextValue(auscultationValue) || ('common.messages.noValue' | transloco) }}
                    </div>
                  } @else {
                    <textarea
                      [value]="getCurrentTextValue(auscultationValue)"
                      (input)="updateTextValue(auscultationValue, $event)"
                      class="w-full min-h-[80px] resize-y"
                      [class.field-modified]="isFieldModified(auscultationValue)"
                      [placeholder]="'common.placeholders.enterValue' | transloco"
                      pTextarea
                      [autoResize]="true">
                    </textarea>
                  }
                </div>
              }

              @if (auscultationValue.info.type === InfoType.CHOICE) {
                <div>
                  @if (isReadOnly()) {
                    <ces-property label="">
                      {{ getCurrentChoiceDisplayValue(auscultationValue) || ('common.messages.noChoiceSelected' | transloco) }}
                    </ces-property>
                  } @else {
                    @let choiceOptions = ausc.getEditableChoiceOptions(auscultationValue.info)();
                    <p-select
                      [ngModel]="getCurrentChoiceValue(auscultationValue)"
                      (ngModelChange)="updateChoiceValue(auscultationValue, $event)"
                      [options]="choiceOptions"
                      optionLabel="name"
                      optionValue="id"
                      [showClear]="true"
                      appendTo="body"
                      [filter]="choiceOptions.length >= 10"
                      [placeholder]="'common.placeholders.selectOption' | transloco"
                      class="w-full"
                      [class.field-modified]="isFieldModified(auscultationValue)" />
                  }
                </div>
              }

              @if (auscultationValue.info.type === InfoType.FILE || auscultationValue.info.type === InfoType.IMAGE) {
                <div>
                  <div class="flex flex-col gap-(--gap-tight)">
                    @if (!isReadOnly()) {
                      <ces-button-secondary
                        [label]="
                          (auscultationValue.info.type === InfoType.FILE
                            ? 'entities.auscultationInformation.buttons.addDocument'
                            : 'entities.auscultationInformation.buttons.addPhoto'
                          ) | transloco
                        "
                        icon="add"
                        size="small"
                        (onClick)="triggerFileSelection(auscultationValue)"
                        class="mb-(--space-1)" />
                    }

                    @for (file of ausc.getDisplayedFiles(auscultationValue.campaignInfo.id)(); track file.id) {
                      <div class="flex items-center">
                        <button
                          type="button"
                          class="flex items-center text-(color:--text-disabled) font-semibold cursor-pointer text-left"
                          [ngClass]="{
                            'text-red-500': !isReadOnly() && file.pendingRemoval,
                            'text-teal-600 font-light': !isReadOnly() && file.isPending && !file.pendingRemoval,
                            'text-(color:--text-primary)': isReadOnly(),
                            'hover:text-blue-500': isReadOnly(),
                          }"
                          [attr.aria-label]="file.name"
                          (click)="onFileClick(file)">
                          <ces-icon size="md" class="text-(color:--text-disabled)" aria-hidden="true">{{
                            !isReadOnly() && file.isPending && !file.pendingRemoval
                              ? 'note_add'
                              : !isReadOnly() && file.pendingRemoval
                                ? 'scan_delete'
                                : 'description'
                          }}</ces-icon>
                          <span
                            class="text-sm truncate"
                            [ngClass]="{
                              'line-through': !isReadOnly() && file.pendingRemoval,
                            }">
                            {{ file.name }}
                          </span>
                        </button>
                        @if (!isReadOnly()) {
                          <button
                            type="button"
                            class="flex items-center cursor-pointer hover:text-red-500 active:font-bold active:text-shadow-lg active:text-red-600"
                            [attr.aria-label]="(file.pendingRemoval ? 'common.buttons.restore' : 'common.buttons.remove') | transloco"
                            (click)="
                              file.pendingRemoval
                                ? cancelFileRemoval(auscultationValue, file.id)
                                : removeFile(auscultationValue, file.id, file.isPending && !file.pendingRemoval)
                            ">
                            <ces-icon size="md" aria-hidden="true">{{ file.pendingRemoval ? 'add' : 'close' }}</ces-icon>
                          </button>
                        }
                      </div>
                    }
                  </div>
                </div>
              }

              @if (ausc.validationErrors().get(auscultationValue.campaignInfo.id)) {
                <div class="text-red-500 text-sm mt-(--space-1)">
                  {{ ausc.validationErrors().get(auscultationValue.campaignInfo.id) }}
                </div>
              }
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template #emptymessage>
        <tr>
          <td colspan="4" class="text-center text-(color:--text-secondary) italic py-(--space-8)">
            {{ 'common.messages.noResults' | transloco }}
          </td>
        </tr>
      </ng-template>
    </p-table>
  }

  @if (ausc.isLoading() && ausc.hasGlobalChanges()) {
    <ces-loading-spinner context="inline" size="sm" [message]="'common.messages.saving' | transloco" [vertical]="false" />
  }

  @if (!isReadOnly() && pageStore.isAuscultationReady() && ausc.auscultationValues().length > 0 && ausc.hasGlobalChanges()) {
    <ces-bottom-actions
      [cancelDisabled]="!ausc.hasGlobalChanges() || ausc.isLoading()"
      [cancelLabel]="'common.buttons.cancel' | transloco"
      [validateDisabled]="!ausc.hasGlobalChanges() || ausc.isLoading()"
      [validateLabel]="'common.buttons.saveChanges' | transloco"
      (discard)="onCancel()"
      (validate)="onSave()" />
  }
</div>
